/**
 * Find Time — user-facing option set.
 *
 * Matches legacy wording exactly so the backend `schedule_type`
 * strings round-trip cleanly (see `../state/reducers.ts` +
 * payload builder).
 */

/**
 * The discriminant the form stores. `null` = "user has not picked a
 * Find Time option" (custom mode). Any other value is one of the
 * legacy strings the backend accepts.
 */
export type FindTimeValue =
  | null
  /** Main button action — fill every weekday in the current week
   *  (today through Sunday), regardless of where the calendar is
   *  currently navigated. */
  | 'this_week'
  | 'next_weekday'
  | 'next_2_weekdays'
  | 'next_3_weekdays'
  | 'next_4_weekdays'
  | 'next_5_weekdays'
  | 'next_week'
  | 'next_2_weeks';

export interface FindTimeMode {
  value: Exclude<FindTimeValue, null>;
  /** Base label — the renderer may append a dynamic range. */
  baseLabel: string;
  /** Legacy backend string — sent in payload as `schedule_type`. */
  scheduleType: string;
}

/**
 * The five "next N weekdays" modes + the two "next week(s)" modes.
 * Order matches the Figma dropdown (top-to-bottom).
 */
export const FIND_TIME_MODES: FindTimeMode[] = [
  {
    value: 'next_weekday',
    baseLabel: 'Next weekday',
    scheduleType: 'Next weekday',
  },
  {
    value: 'next_2_weekdays',
    baseLabel: 'Next 2 weekdays',
    scheduleType: 'Next 2 weekdays',
  },
  {
    value: 'next_3_weekdays',
    baseLabel: 'Next 3 weekdays',
    scheduleType: 'Next 3 weekdays',
  },
  {
    value: 'next_4_weekdays',
    baseLabel: 'Next 4 weekdays',
    scheduleType: 'Next 4 weekdays',
  },
  {
    value: 'next_5_weekdays',
    baseLabel: 'Next 5 weekdays',
    scheduleType: 'Next 5 weekdays',
  },
  {
    value: 'next_week',
    baseLabel: 'Next week',
    scheduleType: 'Next week',
  },
  {
    value: 'next_2_weeks',
    baseLabel: 'Next 2 weeks',
    scheduleType: 'Next 2 weeks',
  },
];

/** Number of target weekdays for each `next_N_weekdays` mode. */
export function weekdaysTargetFor(value: FindTimeValue): number | null {
  switch (value) {
    case 'next_weekday':
      return 1;
    case 'next_2_weekdays':
      return 2;
    case 'next_3_weekdays':
      return 3;
    case 'next_4_weekdays':
      return 4;
    case 'next_5_weekdays':
      return 5;
    default:
      return null;
  }
}

/**
 * Backend `schedule_type` for the self-schedule payload — always
 * `'Custom'`.
 *
 * The scheduler revamp moved all slot generation to the FE: every
 * pick (incl. the Find Time presets) is sent explicitly in
 * `time_slots`, so the BE does no generation off `schedule_type`. The
 * BE `AtsSchedule::SCHEDULE_TYPES` enum has no `"Next 2 weeks"` (a
 * revamp-only option), so echoing the picker label 422'd with
 * `err_schedule_type_invalid`. `Custom` is the only value that stays
 * valid for every option and matches legacy's custom-slots path
 * (`useSchedulerDetailsPayload`). Decision: deeshath + karthikeyan,
 * offline (SCH-41).
 */
export function scheduleTypeFor(_value: FindTimeValue): string {
  return 'Custom';
}
